


































import Vue from 'vue';
import { mapActions } from 'vuex';
import { parseISO } from 'date-fns';
import { intersection, cloneDeep, difference, first, compact, union } from 'lodash';

import { BFormCheckbox, BFormCheckboxGroup, VBTooltip } from 'bootstrap-vue';
import { AuthMode, AuthenticationSettings } from '../types';

export default Vue.extend({
  name: 'AuthenticationTypeSelection',
  components: {
    BFormCheckbox,
    BFormCheckboxGroup,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  props: {
    authenticationSettings: {
      type: Object as () => AuthenticationSettings,
      default: {},
      required: true,
    },
  },
  data() {
    const overrides = compact([
      this.authenticationSettings.overrideEnableStandaloneLogin ? 'standalone' : '',
      this.authenticationSettings.overrideEnableGoogleLogin ? 'google' : '',
    ]);
    return {
      selectedAuthModes: union(this.authenticationSettings.selectedAuthModes, overrides),
    };
  },
  computed: {
    samlConfigurationValidated(): boolean {
      return (
        this.authenticationSettings.lastLoggedInWithSaml != null &&
        this.authenticationSettings.lastEditionDateSaml != null &&
        parseISO(this.authenticationSettings.lastLoggedInWithSaml + 'Z') >
          parseISO(this.authenticationSettings.lastEditionDateSaml + 'Z')
      );
    },
    alternativesAuthModes() {
      let alternatives = [AuthMode.Standalone];
      if (this.samlConfigurationValidated && this.authenticationSettings.samlConfigurationReady) {
        alternatives.push(AuthMode.SAML);
      }
      if (this.authenticationSettings.googleConfigurationReady) {
        alternatives.push(AuthMode.Google);
      }
      return alternatives;
    },
    selectedAlternativesAuthModes() {
      return intersection(this.selectedAuthModes, this.alternativesAuthModes);
    },
    labels(): object {
      return {
        [AuthMode.Standalone]: 'Username and Password',
        [AuthMode.SAML]: 'SAML 2.0',
        [AuthMode.Google]: 'Google Sign-In',
      };
    },
    options(): object[] {
      return [
        {
          text: this.labels[AuthMode.Standalone],
          value: AuthMode.Standalone,
          disabled: this.authenticationSettings.overrideEnableStandaloneLogin,
        },
        {
          text: this.labels[AuthMode.SAML],
          value: AuthMode.SAML,
          disabled: false,
        },
        {
          text: this.labels[AuthMode.Google],
          value: AuthMode.Google,
          disabled: this.authenticationSettings.overrideEnableGoogleLogin,
        },
      ];
    },
    debugMode: {
      get() {
        return this.authenticationSettings.debugMode;
      },
      set(debugMode: boolean) {
        this.$emit('update-authentication-settings', {
          ...this.authenticationSettings,
          debugMode,
        });
      },
    },
  },
  watch: {
    authenticationSettings() {
      this.selectedAuthModes = cloneDeep(this.authenticationSettings.selectedAuthModes);
    },
  },
  methods: {
    ...mapActions({
      showAlert: 'alerts/show',
    }),
    async setSelectedAuthModes(selectedAuthModes: string[]) {
      const alternative = intersection(selectedAuthModes, this.alternativesAuthModes);
      const disabled = first(difference(this.selectedAuthModes, selectedAuthModes));
      const enabled = first(difference(selectedAuthModes, this.selectedAuthModes));

      if (enabled == AuthMode.SAML && !this.authenticationSettings.samlConfigurationReady) {
        // SAML configuration not ready.
        await this.$nextTick();
        this.selectedAuthModes = cloneDeep(this.authenticationSettings.selectedAuthModes);

        this.showAlert({
          type: 'warning',
          title: 'Heads up!',
          message: 'To enable SAML 2.0 authentication you need to complete the setup first.',
        });
      } else if (alternative.length == 0) {
        // Prevent lock out due lack of authentication methods.
        await this.$nextTick();
        this.selectedAuthModes = cloneDeep(this.authenticationSettings.selectedAuthModes);

        this.showAlert({
          type: 'warning',
          title: 'Heads up!',
          message: `You should setup another authentication method if you want to disable ${
            this.labels[first(this.selectedAlternativesAuthModes)]
          }`,
        });
      } else {
        // Accepted actions
        if (disabled) {
          this.showAlert({
            type: 'warning',
            title: 'Heads up!',
            message: `All users who currently have access only via ${this.labels[disabled]} won't be able to access ZenGRC instance anymore`,
          });
        }

        this.$emit('update-authentication-settings', {
          ...this.authenticationSettings,
          selectedAuthModes,
        });
      }
    },
  },
});
